import { useMediaQuery } from '@mui/material';
import { theme, designSystem } from '../styles/theme';
import { useRecoilState } from 'recoil';
import { isLoggedInAtom, userAtom, hasAuthHint } from '@/state/loginState';
import { useState } from 'react';
import { api } from '@/api/repository/axios';
import { useEffect } from 'react';
import { profileCache } from '@/utils/profileCache';
import { useNavigate } from 'react-router-dom';
import { createFeatureStatusStore, useFeatureStatus } from '@/common/featureStatus';

export const useIsMobile = () => useMediaQuery(theme.breakpoints.down('md'));

// summary 재검증은 앱 전역에서 하나만 공유 — useAuth는 Header 외에 각 페이지에서도 쓰여
// 라우팅/동시 마운트마다 /users/me/summary가 중복 호출되는 것을 막는다.
// 최근 재검증 후 SUMMARY_REVALIDATE_MS 안에는 캐시만 쓰고 호출을 생략한다(상태 변화 반영은 이 주기로 충분).
const SUMMARY_REVALIDATE_MS = 30_000;
let summaryInFlight: Promise<any> | null = null;
let summaryRevalidatedAt = 0;

const revalidateSummary = (): Promise<any> | null => {
    if (summaryInFlight) {
        return summaryInFlight;
    }
    if (Date.now() - summaryRevalidatedAt < SUMMARY_REVALIDATE_MS && profileCache.get()) {
        return null;
    }
    summaryInFlight = api.user.summary({ silent: true }).then(res => {
        // 성공 시에만 throttle 갱신 — 실패(쿠키 만료 등)면 다음 마운트에서 즉시 재시도
        summaryRevalidatedAt = Date.now();
        return res;
    }).finally(() => {
        summaryInFlight = null;
    });
    return summaryInFlight;
};

export const useAuth = () => {
    const [isLoggedIn, setIsLoggedIn] = useRecoilState(isLoggedInAtom);
    const [, setUser] = useRecoilState(userAtom);
    const navigate = useNavigate();

    // httpOnly 쿠키는 JS에서 읽을 수 없으므로 API로 로그인 상태 확인.
    // 단, 로그인 힌트(localStorage)가 있을 때만 호출한다.
    // 힌트가 없으면 비로그인으로 간주해 불필요한 /users/profile 401 호출을 막는다.
    // 호출이 실패(쿠키 만료 등)하면 로그아웃 처리하여 힌트도 정리된다(self-healing).
    useEffect(() => {
        const path = window.location.pathname;
        if (path.startsWith('/login') || path.startsWith('/signup')) {
            return;
        }
        if (!isLoggedIn && !hasAuthHint()) {
            return;
        }
        // 캐시를 먼저 반영해 즉시 렌더하고(중복 호출은 Header가 캐시를 읽어 해소),
        // 백그라운드로 최신 summary를 재검증해 면허 인증·치과 승인 등 상태 변화가 5분 캐시에 갇히지 않게 한다
        const cached = profileCache.get();
        if (cached) {
            setIsLoggedIn(true);
            setUser(cached);
        }
        // 재검증은 전역 공유(중복 호출 차단) — 최근에 이미 재검증했으면 캐시만 사용
        const revalidation = revalidateSummary();
        if (!revalidation) {
            return;
        }
        revalidation.then(res => {
            if (res.result) {
                setIsLoggedIn(true);
                setUser(res.result);
                profileCache.set(res.result);
            } else {
                setIsLoggedIn(false);
            }
        }).catch(() => {
            setIsLoggedIn(false);
        });
    }, []);


    const logout = async () => {
        try {
            await api.auth.logout();
        } catch (e) {
            // 서버 로그아웃 실패해도 클라이언트 정리 진행
        }
        // 클라이언트 측 쿠키 삭제 (localhost 등 도메인 불일치 대응)
        document.cookie = 'MAC=; Max-Age=0; path=/;';
        document.cookie = 'MAC=; Max-Age=0; path=/; domain=' + window.location.hostname + ';';
        setUser(null);
        setIsLoggedIn(false);
        profileCache.clear();
        navigate('/');
    };

    return { isLoggedIn, setIsLoggedIn, logout };
};

export const useGetHeaderHeight = () => {
    // const isMobile = useIsMobile();
    // return isMobile ? '124px' : '134px';
    return '134px';
};

export const useValidateInput = () => {
    const [validateMessage, setValidateMessage] = useState("");
    const [isValidColor, setIsValidColor] = useState<string | undefined>("");
    const [isSuss, setIsSuss] = useState(false);

    const handleBlur = async (value: string, type: "id" | "nickName" | "email") => {
        if (value.length > 0) {
            // 아이디일 경우 한글, 특수문자 체크
            if (type === "id") {
                const koreanRegex = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;
                const validIdRegex = /^[a-zA-Z0-9_]+$/;

                if (koreanRegex.test(value) || !validIdRegex.test(value)) {
                    setValidateMessage("영문, 숫자만 사용가능");
                    setIsValidColor("#FF0000");
                    setIsSuss(false);
                    return;
                }
            }

            // 이메일은 형식 검증 후 서버에 중복 확인
            if (type === "email") {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    setValidateMessage("이메일 형식이 올바르지 않습니다");
                    setIsValidColor("#FF0000");
                    setIsSuss(false);
                    return;
                }
            }

            const response = type === "id"
                ? await api.idValidate(value)
                : type === "nickName"
                    ? await api.nickNameValidate(value)
                    : await api.emailValidate(value);

            const responseMessage = response.result ? "사용가능" : "사용불가";
            const textColors = response.result ? designSystem.colors.primary : "#FF0000";

            setValidateMessage(responseMessage);
            setIsValidColor(textColors);
            setIsSuss(response.result);
        }
    };

    return { isSuss, validateMessage, isValidColor, handleBlur };
};



// 포인트·세미나 메뉴는 동일한 기능 상태를 사용한다.
// 기능 공개 설정은 서버에서 읽고, 사용자별로 캐시한다.
interface HomeFeatureFlags {
    pointEnabled: boolean;
    seminarPaymentEnabled: boolean;
    seminarPaymentHistoryVisible: boolean;
}

const homeFeatureStore = createFeatureStatusStore<HomeFeatureFlags>(
    async () => {
        const res = await api.point.getFeatureFlags();
        const enabled = !!res?.result?.seminarPayment?.enabled;
        const historyVisible = res?.result?.seminarPayment?.historyVisible;
        return {
            pointEnabled: !!res?.result?.point?.enabled,
            seminarPaymentEnabled: enabled,
            seminarPaymentHistoryVisible: typeof historyVisible === 'boolean' ? historyVisible : enabled,
        };
    },
    { pointEnabled: false, seminarPaymentEnabled: false, seminarPaymentHistoryVisible: false },
);

export const usePointEnabled = () => {
    const { value } = useFeatureStatus(homeFeatureStore);
    return { pointEnabled: value?.pointEnabled ?? false, pointFlagLoaded: value !== null };
};

export const useSeminarPaymentEnabled = () => {
    const { value } = useFeatureStatus(homeFeatureStore);
    return {
        seminarPaymentEnabled: value?.seminarPaymentEnabled ?? false,
        seminarPaymentHistoryVisible: value?.seminarPaymentHistoryVisible ?? false,
        seminarPaymentFlagLoaded: value !== null,
    };
};

// 명치사 AI 공개 상태 — GET /ai-chats/status. 로그인 쿠키를 함께 보내 그 회원 기준(전체 공개 또는 화이트리스트)으로 받는다.
// 커리어와 같이 5분 TTL·앱이 앞으로 올 때·로그인/로그아웃 시 다시 조회한다. 조회 실패는 꺼진 것으로.
const aiChatStatusStore = createFeatureStatusStore<boolean>(
    async () => {
        const res = await api.aiChat.getStatus();
        return res?.result?.enabled === true;
    },
    false,
);

/**
 * @returns aiChatEnabled 지금 보는 사람에게 AI 버튼을 보일지
 * @returns aiChatStatusChecked 지금 보는 사람 기준 값을 한 번이라도 받았는지(받기 전에는 꺼진 것으로 단정하지 않는다)
 * @returns markAiChatDisabled 다른 호출(access 등)에서 꺼진 것을 알았을 때 바로 반영
 */
export const useAiChatEnabled = () => {
    const { value, viewerKey } = useFeatureStatus(aiChatStatusStore);
    return {
        aiChatEnabled: value === true,
        aiChatStatusChecked: value !== null,
        markAiChatDisabled: () => aiChatStatusStore.set(viewerKey, false),
    };
};

// 강사 정산 메뉴 — 커리어가 공개 중이거나, 꺼졌어도 강사 본인에게 판매 기록(정산 예정·정산서)이 있으면 보인다.
// 정산 API(/seminar/instructor/settlements)는 커리어 스위치와 상관없이 열려 있어 그 응답으로 판단한다(커리어가 꺼졌을 때만 부른다).
const instructorSalesStore = createFeatureStatusStore<boolean>(
    async () => {
        const res = await api.seminar.settlementOverview({ silent: true });
        const overview = res?.result;
        return !!overview && ((overview.upcoming?.length ?? 0) > 0 || (overview.settlements?.length ?? 0) > 0);
    },
    false,
);

/**
 * @param isInstructor 강사 회원인지 — 아니면 정산 조회를 하지 않고 숨긴다
 * @returns settlementVisible 강사 정산 메뉴·화면을 보일지
 * @returns settlementLoaded 판단이 끝났는지
 */
export const useInstructorSettlementVisible = (isInstructor: boolean) => {
    const { seminarPaymentEnabled, seminarPaymentFlagLoaded } = useSeminarPaymentEnabled();
    const needSales: boolean = isInstructor && seminarPaymentFlagLoaded && !seminarPaymentEnabled;
    const { value: hasSales } = useFeatureStatus(instructorSalesStore, needSales);
    return {
        settlementVisible: isInstructor && (seminarPaymentEnabled || hasSales === true),
        settlementLoaded: seminarPaymentFlagLoaded && (!needSales || hasSales !== null),
    };
};
